import _ from 'lodash'
import store from '@ps/store'

import { saveFile } from '@ps/api/editor/file'
import router from '@ps/router'
import {
  generateLanguageConfigTextByNode,
  getLanguageConfig,
  getLanguageTextValue,
} from '@ps/bridge/qc/scripts/language'
import {
  executeAction,
  getAddLanguageAction,
  getDeleteLanguageAction,
  getUpdateLanguageAction,
  getUpdateDefaultLanguageAction,
} from '@ps/workbench/undo-redo'
import * as editorScript from '@ps/bridge/qc/scripts/editor'
import gameIframe from '@ps/bridge/iframe/iframe'
import { getSwitchLanguageAction } from '@ps/workbench/undo-redo'
import filePath from '@ps/bridge/path'
import dialog from './dialog'
import { getLanguage as getDefaultLanguage } from '@ps/lang/index'
import { getLanguagesFromGamePlay } from '@ps/workbench/language'
import { getDescendantsText } from '@ps/bridge/qc/node/utils'
import { isTempNode } from '@ps/bridge/qc/base'
import * as languageScript from '@ps/bridge/qc/scripts/language'
import {
  styleAttrsNeedToSeparate,
  setFont,
  getFont,
} from '@ps/bridge/qc/node/text'
import { getUnSplitPropValue } from '@ps/bridge/qc/scripts/editor'
import { specialNodes } from '@ps/bridge/qc/node/config'
import { getScript } from '@ps/bridge/qc/scripts/language'
import { getTextByFont } from '@ps/workbench/layer/text'

// 获取另一个屏幕
const antherAdapter = (adapter) => {
  if (adapter === 'pdef') {
    return 'ldef'
  }
  return 'pdef'
}

function productTextConfig() {
  const gamePlay = gameIframe.gamePlay
  const texts = getDescendantsText(gamePlay)
  let textConfig = {}
  // 从节点中获取多语言信息
  texts.forEach((node) => {
    const editorConfig = editorScript.getScript(node)
    const oldLangKey = editorConfig.$data.oldLangKey
    const langConfig = languageScript.getScript(node)
    // 有原始 key 说明是从 pt 通用模板转过来的素材包自带的节点，这种节点才需要 textConfig 配置
    if (oldLangKey) {
      textConfig = {
        ...textConfig,
        [oldLangKey]: {
          // 竖屏 key
          key_V: `${langConfig.langKey}_V`,
          // 横屏 key
          key_H: `${langConfig.langKey}_H`,
          // 节点 uuid
          uuid: node.uuid,
          // 该文本配置原始 key（从 pt 通用模板转过来时携带的 key）
          key: oldLangKey,
          // 是否临时节点
          isTempNode: isTempNode(node),
        },
      }
    }
  })
  return textConfig
}

export default {
  namespaced: true,

  state: {
    currentLanguage: '',
    defaultLanguage: '',
    languageList: [],
  },

  mutations: {
    updateLanguageList(state, value) {
      state.languageList = value
    },
  },

  actions: {
    /**
     * 初始化获取默认语言
     */
    async initDefaultLang({ rootState, state }) {
      const gamePlay = gameIframe.gamePlay
      const ped = editorScript.getScript(gamePlay)
      if (!ped.languageConfig.defaultLang) {
        ped.languageConfig.defaultLang = getDefaultLanguage()
      }
      state.defaultLanguage = ped.languageConfig.defaultLang
      return ped.languageConfig.defaultLang
    },

    /**
     * 设置默认语言
     * @param {string} lang 默认的语言
     */
    updateDefaultLanguage({ rootState, state }, { lang, record = true }) {
      lang = lang.toLowerCase()

      // 如果切换的语言和当前语言一样，则不需要记录历史记录
      if (lang === state.defaultLanguage) record = false

      executeAction({
        action: getUpdateDefaultLanguageAction({
          newValue: {
            lang,
          },
          oldValue: {
            lang: state.defaultLanguage,
          },
        }),
        record,
      })
    },

    /**
     * 设置当前语言
     * @param {string} lang 当前的语言
     */
    updateCurrentLanguage({ rootState, state }, { lang }) {
      lang = lang.toLowerCase()
      const gamePlay = rootState.workspace.game.gamePlay
      let ped = gamePlay.getScript('playsmart.editor.data')
      if (typeof ped === 'undefined') {
        ped = gamePlay.addScript('playsmart.editor.data')
      }
      ped.languageConfig.currentLang = lang
      state.currentLanguage = lang
      // 切换iframe 当前预览语言值
      gameIframe.iframe.MW_LIVE_PREVIEW_LANGUAGE = lang
    },
    /**
     * 初始化语言列表
     */
    async initLanguageList({ rootState, state }) {
      const gamePlay = rootState.workspace.game.gamePlay
      const ped = gamePlay.getScript('playsmart.editor.data')
      if (ped.languageConfig.langList) {
        const langList = [...Object.values(ped.languageConfig.langList)]
        state.languageList = langList
      } else {
        ped.languageConfig.langList = [getDefaultLanguage()]
        state.languageList = ped.languageConfig.langList
      }
    },
    /**
     * 更新当前语言的文本样式和文本内容
     * @param {string} 更新的语言
     * @param {string} 更新的节点
     * @param {string} 更新的来源,用于某些属性区分横竖屏
     * @returns
     */
    updateLanguageConfigByNode({ rootState }, { lang, node, source }) {
      lang = lang.toLowerCase()
      const language = node.getScript('ps.Languages')
      const adapterResolution =
        rootState.workspace.game.device.adapterResolution
      let innerStyleAttrNeedToSepareate = styleAttrsNeedToSeparate
      const isUpSplit = getUnSplitPropValue(node, `fontSize${lang}`)
      // 如果不需要拆分 fontSize， 则直接更新双分辨率数据
      if (isUpSplit) {
        innerStyleAttrNeedToSepareate = []
      } else {
        innerStyleAttrNeedToSepareate = styleAttrsNeedToSeparate
      }
      if (innerStyleAttrNeedToSepareate.includes(source)) {
        language.langConfig[lang][adapterResolution] =
          generateLanguageConfigTextByNode(node)
      } else {
        language.langConfig[lang][adapterResolution] =
          generateLanguageConfigTextByNode(node)
        const adapter = antherAdapter(adapterResolution)
        // 如果不支持拆分，直接同步当前分辨率的字体数据到另外一个分辨率下
        if (isUpSplit) {
          language.langConfig[lang][adapter] =
            generateLanguageConfigTextByNode(node)
        } else {
          // 如果支持拆分的话，需要传 adapter 和 lang ，保留之前的 fontSize 数据
          language.langConfig[lang][adapter] = generateLanguageConfigTextByNode(
            node,
            adapter,
            lang,
          )
        }
      }
      delete language.langConfig[lang].name
      delete language.langConfig[lang].style
      delete language.langConfig[lang].value
    },

    /**
     * 新增语言,用兜底语言的配置
     * @param {string} lang 新增语言
     * @param {any} value 每个key的value,如 { text1: 'text1', text2: 'text2'}
     */
    async addLanguage({ state }, { lang, value }) {
      const language = getLanguageConfig(
        state.defaultLanguage,
        store.state.workspace.game.device.adapterResolution,
      )
      _.each(language, (item, key) => {
        language[key].value = value[key]
      })
      executeAction({
        action: getAddLanguageAction({
          newValue: {
            lang: lang.toLowerCase(),
            value: _.cloneDeep(language),
          },
          oldValue: {
            lang: lang.toLowerCase(),
          },
        }),
        record: true,
      })
    },

    /**
     * 删除语言
     * @param {string} lang 删除的语言
     * @param {boolean} record
     */
    deleteLanguage({ state }, { lang, record = true }) {
      executeAction({
        action: getDeleteLanguageAction({
          newValue: {
            lang,
          },
          oldValue: {
            lang,
            value: getLanguageConfig(
              lang,
              store.state.workspace.game.device.adapterResolution,
            ),
          },
        }),
        record,
      })
    },
    /**
     * 用于新增或更新语言,对每个key赋值value
     * @param {lang} 语言
     * @param {info} 每个key的value,如 { text1: 'text1', text2: 'text2'}
     */
    updateLanguageValues({ state }, { lang, info, record = true }) {
      executeAction({
        action: getUpdateLanguageAction({
          newValue: {
            lang,
            text: info,
          },
          oldValue: {
            lang,
            text: getLanguageTextValue(
              lang,
              store.state.workspace.game.device.adapterResolution,
            ),
          },
        }),
        record,
      })
    },

    /**
     * 获取默认语言信息
     * @param {node}
     */
    getDefaultLanguageInfo({ state }) {
      return getLanguageConfig(
        state.defaultLanguage,
        store.state.workspace.game.device.adapterResolution,
      )
    },
    /**
     * 获取指定语言信息
     * @param {node}
     */
    getLanguageInfo({ rootState }, { lang }) {
      return getLanguageConfig(
        lang,
        store.state.workspace.game.device.adapterResolution,
      )
    },
    /**
     * 切换语言
     * @param {string} lang
     * @param {boolean} record
     */
    async switchLanguage({ state }, { lang, record = true }) {
      lang = lang.toLowerCase()

      // 如果切换的语言和当前语言一样，则不需要记录历史记录
      if (lang === state.currentLanguage) record = false

      executeAction({
        action: getSwitchLanguageAction({
          newValue: {
            lang,
          },
          oldValue: {
            lang: state.currentLanguage.toLowerCase(),
          },
        }),
        record,
      })
    },
    /**
     * 复制语言样式到所有语言
     * @param {lang} 需要复制的语言
     * @param {node} 复制的节点key
     */
    async applyLanguageStyleToAll({ rootState, state }, { lang, node, font }) {
      lang = lang.toLowerCase()
      const psLang = node.getScript('ps.Languages')
      let applyAllText = ''
      _.each(psLang.langConfig, (value, key) => {
        if (key !== lang) {
          let realText = ''
          if (typeof value.pdef !== 'undefined') {
            realText = value.pdef.value
          }
          if (typeof value.ldef !== 'undefined') {
            realText = value.ldef.value
          }
          applyAllText = realText + applyAllText
        }
      })
      const fontList = await store.dispatch('config/getFontList')
      const assetFontList = rootState.assets.assetFontList
      await getFont({
        fontName: font,
        text: getTextByFont(font, applyAllText),
        fontList: fontList.concat(assetFontList),
        pid: store.state.workspace.pid,
      })

      executeAction({
        action: {
          execute: ({ newValue }) => {
            const { config, node } = newValue
            const psLang = node.getScript('ps.Languages')
            _.each(psLang.langConfig, (value, key) => {
              // 只对样式应用到其他语言,语言的value不变
              let realValue
              if (config.pdef) {
                realValue =
                  typeof value.pdef.value !== 'undefined'
                    ? value.pdef.value
                    : value.value
                psLang.langConfig[key].pdef = Object.assign({}, config.pdef, {
                  value: realValue,
                })
              }
              if (config.ldef) {
                realValue =
                  typeof value.ldef.value !== 'undefined'
                    ? value.ldef.value
                    : value.value
                psLang.langConfig[key].ldef = Object.assign({}, config.ldef, {
                  value: realValue,
                })
              }
              // 兼容旧数据, 没有pdef,ldef的情况
              if (
                typeof config.pdef === 'undefined' &&
                typeof config.ldef === 'undefined'
              ) {
                psLang.langConfig[key] = Object.assign({}, config, {
                  value: value.value,
                })
              }
            })
          },
          undo: ({ oldValue }) => {
            const { languages, node } = oldValue
            const psLang = node.getScript('ps.Languages')
            _.each(psLang.langConfig, (value, key) => {
              psLang.langConfig[key] = languages[key]
            })
          },
          value: {
            newValue: {
              node,
              config: _.cloneDeep(psLang.langConfig[lang]),
            },
            oldValue: {
              node,
              // 被修改之前的语言信息
              languages: _.cloneDeep(psLang.langConfig),
            },
          },
          methodName: 'applyLanguageStyleToAllAction',
        },
      })
    },

    /**
     * 保存多语言信息到language.json
     */
    async saveLanguageConfig({ state }) {
      // https://confluence.mobvista.com/pages/viewpage.action?pageId=35023010
      const projectKey = router.currentRoute.query.id
      const languagesRes = gameIframe.iframe.languagesMgr.getRes()
      let newLanguagesRes = {}
      newLanguagesRes.fallbackLocale = state.defaultLanguage
      newLanguagesRes.currentLanguage = state.currentLanguage
      newLanguagesRes.languages = getLanguagesFromGamePlay()
      if (_.isEmpty(newLanguagesRes.languages)) {
        newLanguagesRes.languages = {
          [state.defaultLanguage]: {},
        }
      }
      const gamePlayNode = gameIframe.UIRoot.getChild(
        specialNodes.gamePlay.name,
      )
      const gamePlayEditorScriptConfig = editorScript.getScript(gamePlayNode)

      const langList = gamePlayEditorScriptConfig.languageConfig.langList
      // 保存的时候只保存当前语言的配置
      newLanguagesRes.languages = _.pick(newLanguagesRes.languages, langList)

      // 非标准包增加 textConfig，有些文本配置没有关联的文本节点，当 pt 转 ps 后会将 languages.json 中的配置修改
      // 素材包会通过 languageMgr 脚本直接去读取 languages.json 中的配置，但是由于配置已经被修改了
      // 如 download 的文本配置的 key 被改成 abc，这个时候素材包还是使用 download 去获取，获取文本配置失败
      // textConfig 存储新 key 与旧 key 的映射关系，当素材包会通过 languageMgr 脚本去获取的时候，languageMgr 会读取 textConfig 的映射关系返回想要的数据
      if (!store.state.workspace.project.isNormMaterial) {
        const textConfig = productTextConfig()
        if (!_.isEmpty(textConfig)) {
          newLanguagesRes.textConfig = textConfig
        }
      }

      newLanguagesRes.selfDevToPs = Boolean(_.get(languagesRes, 'selfDevToPs'))
      newLanguagesRes = JSON.stringify(newLanguagesRes, null, 2)

      try {
        const res = await saveFile(
          {
            pid: projectKey,
            path: filePath.LANGUAGES_JSON,
          },
          newLanguagesRes,
        )
        if (res.code === 0) {
          return res
        }
        return Promise.reject(new Error(res.msg || '保存language.json失败'))
      } catch (e) {
        return Promise.reject(new Error(e.message || '保存language.json失败'))
      }
    },

    /**
     * 应用到同语言的所有文本
     * @param {lang} 需要复制的语言
     * @param {font} 需要复制的字体
     */
    async applyFontToOtherTextNode({ rootState, state }, { lang, font }) {
      const nodeText = getDescendantsText(gameIframe.gamePlay)
      const nodeUuidFontConfig = {} // 存储替换的历史字体  undo使用
      let applyAllText = ''
      nodeText.forEach((node) => {
        const language = getScript(node)
        // 横/竖屏字体一致，这里取竖屏（pdef）的字体
        const oldFont = language.langConfig[lang].pdef.style.font
        nodeUuidFontConfig[node.uuid] = _.cloneDeep(oldFont)
        // 整理文案; getTextByFont会整理全部使用相同font的文案，这里需要过滤掉相同字体文案输出，防止文案重复整理
        if (font !== oldFont) {
          applyAllText = node.text + applyAllText
        }
      })
      const fontList = await store.dispatch('config/getFontList')
      const assetFontList = rootState.assets.assetFontList
      await getFont({
        fontName: font,
        text: getTextByFont(font, applyAllText),
        fontList: fontList.concat(assetFontList),
        pid: store.state.workspace.pid,
      })

      executeAction({
        action: {
          execute: ({ newValue }) => {
            const { nodeText, lang, font } = newValue
            nodeText.forEach((node) => {
              const language = getScript(node)
              _.set(language.langConfig, `${lang}.pdef.style.font`, font)
              _.set(language.langConfig, `${lang}.ldef.style.font`, font)
              // 设置节点的font
              setFont({ node, fontName: font })
            })
          },
          undo: ({ oldValue }) => {
            const { nodeText, lang, nodeUuidFontConfig } = oldValue
            nodeText.forEach((node) => {
              const language = getScript(node)
              const font = nodeUuidFontConfig[language.langKey]
              _.set(language.langConfig, `${lang}.pdef.style.font`, font)
              _.set(language.langConfig, `${lang}.ldef.style.font`, font)
              setFont({ node, fontName: font })
            })
          },
          value: {
            newValue: { nodeText, lang, font },
            oldValue: { nodeText, lang, nodeUuidFontConfig },
          },
          methodName: 'applyFontToOtherTextNodeAction',
        },
      })
    },
  },

  modules: {
    dialog,
  },
}
